import React from 'react';
import { Link } from "react-router-dom";
import { VaultLayout, VAULT_SUPPORT_EMAIL } from "../components/VaultLayout";

export const VaultPrivacy = ({ theme, onToggleTheme }) => {
  return (
    <VaultLayout
      title="Privacy Policy"
      meta="Vault for iPhone · Effective October 3, 2026"
      theme={theme}
      onToggleTheme={onToggleTheme}
    >
      <div className="vault-summary">
        <p>
          <strong>The short version:</strong> Vault is an offline password manager. Everything you
          save stays on your iPhone. There are no accounts, no servers, no analytics and no ads. We
          do not collect, receive or have any way to see what you store in the app.
        </p>
      </div>

      <h2>Who this policy covers</h2>
      <p>
        This policy applies to the Vault app for iPhone, developed by Erwin Agpasa
        (&ldquo;we&rdquo;, &ldquo;us&rdquo;). It describes what the app stores, where it stores it,
        and what we can and cannot access. It does not cover this website itself.
      </p>

      <h2>Information we collect</h2>
      <p>
        <strong>None.</strong> Vault does not connect to the internet and does not send any
        information to us or to anyone else. The app contains no analytics, advertising or tracking
        software, and it does not use any identifier to track you.
      </p>
      <p>
        If you have chosen to share analytics with app developers in your iPhone settings, Apple may
        give us aggregated crash and usage statistics for the app. That sharing is run by Apple and
        controlled by your device settings; it never includes the contents of your vault.
      </p>

      <h2>Information stored on your iPhone</h2>
      <p>The app keeps the following on your device so that it can work:</p>
      <ul>
        <li>
          <strong>Your vault items</strong> (names, usernames, passwords, card details, notes and
          similar entries). Each item is encrypted with AES-256 before it is written to the
          app&rsquo;s local database.
        </li>
        <li>
          <strong>The encryption key</strong> for your vault, stored in the iOS Keychain.
        </li>
        <li>
          <strong>Your master PIN</strong>, stored only as a salted hash in the iOS Keychain. The
          PIN itself is never saved.
        </li>
        <li>
          <strong>Your reset email address</strong>, stored in the iOS Keychain. It is used only to
          check what you type when you reset a forgotten PIN. It is never sent anywhere, and we never
          email you.
        </li>
        <li>
          <strong>Your app settings</strong> (Face ID unlock, clipboard clearing and auto-lock).
        </li>
      </ul>

      <h2>Face ID</h2>
      <p>
        If you unlock with Face ID, the check is done by iOS. Vault never receives or stores your
        face data; it is only told whether the check succeeded.
      </p>

      <h2>Clipboard</h2>
      <p>
        When you tap Copy, the value is placed on the iPhone clipboard so you can paste it into
        another app. While it is there, other apps you paste into, and other Apple devices signed in
        to your account through Universal Clipboard, may be able to read it. If &ldquo;Clear
        Clipboard After 30s&rdquo; is on, Vault clears the clipboard about 30 seconds after you
        copy, as long as the app is still running.
      </p>

      <h2>Backups and other devices</h2>
      <p>
        Vault has no cloud sync. If you back up your iPhone with iCloud or a computer, iOS may
        include the app&rsquo;s encrypted database in that backup. The encryption key is stored so
        that it stays on the device it was created on and is not transferred to other devices, which
        means a backup copy of the database cannot be read elsewhere, and your vault cannot be moved
        to a new iPhone.
      </p>

      <h2>Sharing</h2>
      <p>
        We do not sell, share or disclose your information, because we never have it.
      </p>

      <h2>Keeping and deleting your data</h2>
      <p>
        Your data stays on your iPhone until you remove it. You can delete any item from inside the
        app. Deleting the app removes the vault database and everything in it. Because of how iOS
        works, the Keychain entries listed above can remain on the device after the app is deleted;
        they cannot be used without the database and are replaced if you install and set up Vault
        again.
      </p>

      <h2>Your part in keeping the vault safe</h2>
      <p>
        Anyone who has your unlocked iPhone and knows your master PIN, or knows your reset email,
        can open the vault. Use a device passcode, choose a PIN that is hard to guess, and treat
        your reset email as something only you should know. No method of storage is completely
        secure, and we cannot guarantee absolute security.
      </p>

      <h2>Children</h2>
      <p>
        Vault is not directed at children, and it does not collect information from anyone,
        including children.
      </p>

      <h2>Changes to this policy</h2>
      <p>
        If the app changes in a way that affects this policy, we will update this page and the
        effective date above.
      </p>

      <h2>Contact</h2>
      <p>
        Questions about this policy can be sent to{' '}
        <a className="vault-link" href={`mailto:${VAULT_SUPPORT_EMAIL}`}>{VAULT_SUPPORT_EMAIL}</a>.
        For help using the app, see the{' '}
        <Link className="vault-link" to="/vault/support">Support page</Link>.
      </p>
    </VaultLayout>
  )
}
